import React, { useState } from "react";
import { headerStyle, sectionStyle } from "./formConstants";
import { useSharedValue } from "../components/Constants/constants";
import "../Forms/style.css";
import { FormCheckBox, FormDate, FormInput, FormTitle, createRequiredLabel } from "./common";
import CountryDropdown from "./CountryDropdown";
import { Form8233Model } from "../Models/Form8233Models";
import { useForm8233 } from "../Hooks/useForm8233";
import { Form8233Service } from "../services/Form8233Service";
import { parseAddressComponents, validateTINFormat } from "../utils/formUtils";

interface Form8233Data {
  [key: string]: string | boolean;
  isSigned: string;
  isModified: string;
}

interface Form8233Props {
  selectedRowData?: any;
  row?: any;
  selectedIndex?: number;
  onSaveSuccess?: (row: any) => void;
}

const Form8233 = ({ selectedRowData, row, selectedIndex = 0, onSaveSuccess }: Form8233Props) => {
  const { isMenuOpen } = useSharedValue();

  // Use Form8233 hook for state management
  const {
    model,
    isLoading,
    isSaving,
    errors,
    isValid,
    updateField,
    updateMultipleFields,
    validate,
  } = useForm8233({
    initialData: selectedRowData,
  });

  // Legacy form data state for UI compatibility
  const [formData, setFormData] = useState<Form8233Data>(() => {
    // Convert model to form data format for legacy UI
    const modelData = model as any;
    return {
      ...modelData,
    };
  });

  // TIN validation state
  const [tinValidationMessage, setTinValidationMessage] = useState<string | null>(null);

  // TIN validation effect - runs on form load and when TIN/Type changes
  React.useEffect(() => {
    const tinValue = formData["Form-8233-Part_1_us_tax_id"];
    const currentType = formData["Taxpayer type"];

    if (tinValue && tinValue.toString().trim() !== "") {
      const trimmedTin = tinValue.toString().trim();
      const typeString = currentType ? currentType.toString() : "";

      // Validate TIN format and get detected type
      const validation = validateTINFormat(trimmedTin, typeString);

      // Set validation message (warnings or success)
      setTinValidationMessage(validation.message);

      // If type was detected and it's different from current, update it
      if (validation.detectedType && validation.detectedType !== typeString) {
        let formType = validation.detectedType;
        if (validation.detectedType === "SSN" || validation.detectedType === "ITIN") {
          formType = "SSN/ITIN";
        }

        updateField("Taxpayer type", formType);
        setFormData((prev: Form8233Data) => ({ ...prev, "Taxpayer type": formType }));
      }
    } else {
      // Clear validation message when TIN is empty
      setTinValidationMessage(null);
    }
  }, [formData["Form-8233-Part_1_us_tax_id"], formData["Taxpayer type"], updateField]);

  // Update formData whenever model changes
  React.useEffect(() => {
    setFormData({ ...model });
  }, [model]);

  React.useEffect(() => {
    if (selectedRowData) {
      // Check if UniqueIdentifier has changed and update it
      const newUniqueIdentifier =
        selectedRowData.uniqueidentifier || selectedRowData.UniqueIdentifier;
      const currentUniqueIdentifier = formData.UniqueIdentifier;

      if (newUniqueIdentifier && newUniqueIdentifier !== currentUniqueIdentifier) {
        updateField("UniqueIdentifier", newUniqueIdentifier);
      }

      // Update other fields that might have changed
      Object.keys(selectedRowData).forEach((key) => {
        const value = selectedRowData[key];
        const currentValue = (formData as any)[key];

        // Skip combined address fields - they should be handled by the Service parsing
        if (
          key === "Form-8233-Part_1_permanent_address_city_state_postal" ||
          key === "Form-8233-Part_1_us_address_city_state_zip"
        ) {
          return;
        }

        // Only update if the value has actually changed and is not empty
        if (value && value !== currentValue && typeof value === "string") {
          updateField(key, value);
        }
      });

      // Special handling for signature and date fields - check multiple possible field names
    }
  }, [selectedRowData, updateField]);

  // State to control validation summary display
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, type, checked, value } = e.target;

    // Handle address field splitting with both legacy form state and model updates
    if (
      name === "Form-8233-Part_1_permanent_address_city_state_postal" ||
      name === "Permanent_address_city_state_postal"
    ) {
      const address = parseAddressComponents(value);

      setFormData((prev: Form8233Data) => ({
        ...prev,
        [name]: value,
        "Permanent address city": address.city,
        "Permanent address state NonUS": address.state,
        "Permanent address zip": address.postalCode,
      }));
      // Update model through hook
      updateMultipleFields({
        [name]: value,
        "Permanent address city": address.city,
        "Permanent address state NonUS": address.state,
        "Permanent address zip": address.postalCode,
      });
    } else if (
      name === "Form-8233-Part_1_us_address_city_state_zip" ||
      name === "8233_US_Address_city/state/town/ZC"
    ) {
      const address = parseAddressComponents(value);

      setFormData((prev: Form8233Data) => ({
        ...prev,
        [name]: value,
        "US address city": address.city,
        "US address state USCA": address.state,
        "US address zip": address.postalCode,
      }));
      // Update model through hook
      updateMultipleFields({
        [name]: value,
        "US address city": address.city,
        "US address state USCA": address.state,
        "US address zip": address.postalCode,
      });
    } else if (name === "Permanent address city") {
      // Check if the city field contains combined address data
      if (value && (value.includes(",") || /\d{4,}/.test(value) || /\s[A-Z]{2}\s/.test(value))) {
        const address = parseAddressComponents(value);

        // Only split if we actually found separate components
        if (address.city !== value || address.state || address.postalCode) {
          setFormData((prev: Form8233Data) => ({
            ...prev,
            "Permanent address city": address.city,
            "Permanent address state NonUS": address.state,
            "Permanent address state USCA": address.state,
            "Permanent address zip": address.postalCode,
          }));
          updateMultipleFields({
            "Permanent address city": address.city,
            "Permanent address state NonUS": address.state,
            "Permanent address state USCA": address.state,
            "Permanent address zip": address.postalCode,
          });
          return;
        }
      }
      // Normal single field update
      setFormData((prev: Form8233Data) => ({ ...prev, [name]: value }));
      updateField(name, value);
    } else if (name === "US address city") {
      // Check if the city field contains combined address data
      if (value && (value.includes(",") || /\d{4,}/.test(value) || /\s[A-Z]{2}\s/.test(value))) {
        const address = parseAddressComponents(value);

        // Only split if we actually found separate components
        if (address.city !== value || address.state || address.postalCode) {
          setFormData((prev: Form8233Data) => ({
            ...prev,
            "US address city": address.city,
            "US address state USCA": address.state,
            "US address state NonUS": address.state,
            "US address zip": address.postalCode,
          }));
          updateMultipleFields({
            "US address city": address.city,
            "US address state USCA": address.state,
            "US address state NonUS": address.state,
            "US address zip": address.postalCode,
          });
          return;
        }
      }
      // Normal single field update
      setFormData((prev: Form8233Data) => ({ ...prev, [name]: value }));
      updateField(name, value);
    } else if (type === "checkbox") {
      const newValue = type === "checkbox" ? checked : value;

      // Update the model through the hook
      updateField(name, newValue);

      // Handle field name synchronization for legacy compatibility
      if (name === "Form-8233-Part_1_individual_name") {
        updateField("Individual_name", newValue);
      } else if (name === "Individual_name") {
        updateField("Form-8233-Part_1_individual_name", newValue);
      }
    } else {
      // Default case for regular text inputs and other field types
      const newValue = type === "checkbox" ? checked : value;

      // Update the legacy formData state
      setFormData((prev: Form8233Data) => ({ ...prev, [name]: newValue }));

      // Update the model through the hook
      updateField(name, newValue);

      // Handle field name synchronization for legacy compatibility
      if (name === "Form-8233-Part_1_individual_name") {
        updateField("Individual_name", newValue);
      } else if (name === "Individual_name") {
        updateField("Form-8233-Part_1_individual_name", newValue);
      }
    }

    // Handle TIN validation
    if (name === "Taxpayer type") {
      // When type changes, clear the TIN input so user can enter new value
      setFormData((prev: Form8233Data) => ({ ...prev, "Form-8233-Part_1_us_tax_id": "" }));
      updateField("Form-8233-Part_1_us_tax_id", "");

      // Clear validation message
      setTinValidationMessage(null);
    } else if (name === "Form-8233-Part_1_us_tax_id") {
      const tinValue = value.toString().trim();
      const currentType = formData["Taxpayer type"];
      const typeString = currentType ? currentType.toString() : "";

      if (tinValue) {
        // Validate TIN format
        const validation = validateTINFormat(tinValue, typeString);

        // Set validation message
        setTinValidationMessage(validation.message);

        // If valid and detected type exists, and NO type is selected, auto-select it
        if (
          validation.isValid &&
          validation.detectedType &&
          (!typeString || typeString === "" || typeString === "---Select---")
        ) {
          let formType = validation.detectedType;
          if (formType === "SSN" || formType === "ITIN") {
            formType = "SSN/ITIN";
          }

          updateField("Taxpayer type", formType);
          setFormData((prev: Form8233Data) => ({ ...prev, "Taxpayer type": formType }));
        }
      } else {
        setTinValidationMessage(null);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear any previous validation errors
    setShowValidationErrors(false);

    if (!validate()) {
      setShowValidationErrors(true);
      alert("Please fill in all required fields before submitting.");
      return;
    }

    try {
      // Use the model directly as the source of truth for current form values
      const currentFormValues: any = {};

      // Get all current form values from the model (which is always up-to-date)
      Object.keys(model).forEach((key) => {
        currentFormValues[key] = (model as any)[key];
      });

      // Set mapping done flag to prevent re-mapping
      currentFormValues.isMapDone = true;

      // Create a new model with the current form values first
      const saveModel = new Form8233Model(currentFormValues);

      // Remove specific fields from the model after creation but before sending to API
      delete (saveModel as any)["Form-8233-Part_1_permanent_address_city_state_postal"];
      delete (saveModel as any)["Form-8233-Part_1_us_address_city_state_zip"];

      // Save directly using the service with our collected current values
      const success = await Form8233Service.saveToApi(saveModel, row, selectedIndex);

      if (success) {
        // Clear validation errors on successful save
        setShowValidationErrors(false);

        if (onSaveSuccess) {
          onSaveSuccess(row);
        }
        alert("Form saved successfully");
      }
    } catch (error) {
      console.error("Failed to save Form 8233:", error);
      alert("Failed to save form data");
    }
  };

  return (
    <div className="scroll-container" style={headerStyle(false)}>
      <div
        style={{ width: "100%", display: "flex", flexDirection: "column", padding: 0, margin: 0 }}
      >
        <form
          onSubmit={handleSubmit}
          style={{
            width: "100% !important",
            minWidth: "100%",
            maxWidth: "100%",
            flex: "1 1 100%",
            display: "flex !important",
            flexDirection: "column",
            margin: "0 !important",
            padding: "0 !important",
            boxSizing: "border-box",
          }}
        >
          <div style={{ display: "flex", width: "100%", flexDirection: "column" }}>
            {/* Validation Summary - Only show after user tries to submit */}
            {showValidationErrors && Object.keys(errors).length > 0 && (
              <div
                style={{
                  backgroundColor: "#fee2e2",
                  border: "1px solid #fecaca",
                  borderRadius: "6px",
                  padding: "12px",
                  margin: "16px 0",
                  color: "#b91c1c",
                }}
              >
                <h4 style={{ margin: "0 0 8px 0", color: "#991b1b" }}>
                  Please fix the following errors:
                </h4>
                <ul style={{ margin: 0, paddingLeft: "20px" }}>
                  {Object.entries(errors).map(([field, error], index) => (
                    <li key={index} style={{ marginBottom: "4px" }}>
                      <strong>
                        {field.replace("Form-8233-Part_", "Part ").replace(/_/g, " ")}:
                      </strong>{" "}
                      {error}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Personal Information */}
            <Section title="Personal Information">
              <div className="space-y-4">
                <FormInput
                  label={createRequiredLabel("Unique Identifier")}
                  id={"UniqueIdentifier"}
                  name={"UniqueIdentifier"}
                  value={(formData["UniqueIdentifier"] as string) || ""}
                  onChange={handleChange}
                />
                <FormInput
                  label={createRequiredLabel("Individual Name")}
                  id={"Form-8233-Part_1_individual_name"}
                  name={"Form-8233-Part_1_individual_name"}
                  value={formData["Form-8233-Part_1_individual_name"] || ""}
                  onChange={handleChange}
                />
                {/* <FormInput label={"Last Name:"} id={"Last_name"} name={"Last_name"} value={formData["Last_name"] || ''} onChange={handleChange} /> */}
              </div>
            </Section>

            {/* Permanent Address */}
            <Section title="Permanent Address">
              <FormInput
                label={"Line 1:"}
                id={"Form-8233-Part_1_permanent_address_street"}
                name={"Form-8233-Part_1_permanent_address_street"}
                value={(formData["Form-8233-Part_1_permanent_address_street"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"Line 2:"}
                id={"Permanent address line 2"}
                name={"Permanent address line 2"}
                value={(formData["Permanent address line 2"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"Line 3:"}
                id={"Permanent address line 3"}
                name={"Permanent address line 3"}
                value={(formData["Permanent address line 3"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"City / Town:"}
                id={"Permanent address city"}
                name={"Permanent address city"}
                value={(formData["Permanent address city"] as string) || ""}
                onChange={handleChange}
              />
              <div style={{ display: "flex", gap: 16, alignItems: "flex-end", marginBottom: 16 }}>
                <div style={{ flex: 1 }}>
                  <label>
                    <span>U.S.& Canada:</span> State / Province:
                    <span
                      style={{ color: "#fbbf24", marginLeft: 4, fontSize: 16 }}
                      title="For U.S. and Canada addresses only"
                    >
                      &#9432;
                    </span>
                  </label>
                  <FormInput
                    label={""}
                    id={"Permanent address state USCA"}
                    name={"Permanent address state USCA"}
                    value={(formData["Permanent address state USCA"] as string) || ""}
                    onChange={handleChange}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label>
                    <span>Non U.S.:</span> State / Province as it appears on form:
                  </label>
                  <FormInput
                    label={""}
                    id={"Permanent address state NonUS"}
                    name={"Permanent address state NonUS"}
                    value={(formData["Permanent address state NonUS"] as string) || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <FormInput
                label={"Zip / Postal Code:"}
                id={"Permanent address zip"}
                name={"Permanent address zip"}
                value={(formData["Permanent address zip"] as string) || ""}
                onChange={handleChange}
              />
              <CountryDropdown
                label="Country:"
                name="Form-8233-Part_1_permanent_address_country"
                id="Form-8233-Part_1_permanent_address_country"
                value={
                  typeof formData["Form-8233-Part_1_permanent_address_country"] === "string"
                    ? formData["Form-8233-Part_1_permanent_address_country"]
                    : ""
                }
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "Form-8233-Part_1_permanent_address_country",
                      value: e.target.value,
                      type: "select-one",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
              />
              <FormInput
                label={"Other:"}
                id={"Permanent address other"}
                name={"Permanent address other"}
                value={(formData["Permanent address other"] as string) || ""}
                onChange={handleChange}
              />

              {/* Yes/No radios */}
              <div style={{ marginTop: 24 }}>
                <div style={{ marginBottom: 16 }}>
                  <label>
                    Is care-of-address: <span style={{ color: "red" }}>*</span>
                  </label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="Permanent address is care of"
                      value="yes"
                      checked={formData["Permanent address is care of"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="Permanent address is care of"
                      value="no"
                      checked={formData["Permanent address is care of"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label>
                    Is P.O. Box: <span style={{ color: "red" }}>*</span>
                  </label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="Permanent address is po box"
                      value="yes"
                      checked={formData["Permanent address is po box"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="Permanent address is po box"
                      value="no"
                      checked={formData["Permanent address is po box"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label>
                    Is rural route: <span style={{ color: "red" }}>*</span>
                  </label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="Permanent address is rural route"
                      value="yes"
                      checked={formData["Permanent address is rural route"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="Permanent address is rural route"
                      value="no"
                      checked={formData["Permanent address is rural route"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                </div>
              </div>
            </Section>

            {/* Address in United States */}
            <Section title="Address in United States">
              <FormInput
                label={"Line 1:"}
                id={"Form-8233-Part_1_us_address_street"}
                name={"Form-8233-Part_1_us_address_street"}
                value={(formData["Form-8233-Part_1_us_address_street"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"Line 2:"}
                id={"US address line 2"}
                name={"US address line 2"}
                value={(formData["US address line 2"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"Line 3:"}
                id={"US address line 3"}
                name={"US address line 3"}
                value={(formData["US address line 3"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"City / Town:"}
                id={"US address city"}
                name={"US address city"}
                value={(formData["US address city"] as string) || ""}
                onChange={handleChange}
              />
              <div style={{ display: "flex", gap: 16, alignItems: "flex-end", marginBottom: 16 }}>
                <div style={{ flex: 1 }}>
                  <label>
                    <span>U.S.& Canada:</span> State / Province:
                    <span
                      style={{ color: "#fbbf24", marginLeft: 4, fontSize: 16 }}
                      title="For U.S. and Canada addresses only"
                    >
                      &#9432;
                    </span>
                  </label>
                  <FormInput
                    label={""}
                    id={"US address state USCA"}
                    name={"US address state USCA"}
                    value={(formData["US address state USCA"] as string) || ""}
                    onChange={handleChange}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label>
                    <span>Non U.S.:</span> State / Province as it appears on form:
                  </label>
                  <FormInput
                    label={""}
                    id={"US address state NonUS"}
                    name={"US address state NonUS"}
                    value={(formData["US address state NonUS"] as string) || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <FormInput
                label={"Zip / Postal Code:"}
                id={"US address zip"}
                name={"US address zip"}
                value={(formData["US address zip"] as string) || ""}
                onChange={handleChange}
              />
              <CountryDropdown
                label="Country:"
                name="US address country"
                id="US address country"
                value={
                  typeof formData["US address country"] === "string"
                    ? formData["US address country"]
                    : ""
                }
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "US address country",
                      value: e.target.value,
                      type: "select-one",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
              />
              <FormInput
                label={"Other:"}
                id={"US address other"}
                name={"US address other"}
                value={(formData["US address other"] as string) || ""}
                onChange={handleChange}
              />

              {/* Yes/No radios */}
              <div style={{ marginTop: 24 }}>
                <div style={{ marginBottom: 16 }}>
                  <label>Is care-of-address:</label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="US address is care of"
                      value="yes"
                      checked={formData["US address is care of"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="US address is care of"
                      value="no"
                      checked={formData["US address is care of"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label>Is P.O. Box:</label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="US address is po box"
                      value="yes"
                      checked={formData["US address is po box"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="US address is po box"
                      value="no"
                      checked={formData["US address is po box"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label>Is rural route:</label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="US address is rural route"
                      value="yes"
                      checked={formData["US address is rural route"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="US address is rural route"
                      value="no"
                      checked={formData["US address is rural route"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                </div>
              </div>
            </Section>

            {/* Taxpayer Identification */}
            <Section title="Taxpayer identification">
              <FormInput
                label="Email:"
                id="Email"
                name="Email"
                value={(formData["Email"] as string) || ""}
                onChange={handleChange}
              />
              <div style={{ marginBottom: 16 }}>
                <label htmlFor="Taxpayer type" style={{ display: "block", marginBottom: 4 }}>
                  Type:
                </label>
                <select
                  id="Taxpayer type"
                  name="Taxpayer type"
                  value={
                    typeof formData["Taxpayer type"] === "string" ? formData["Taxpayer type"] : ""
                  }
                  onChange={(e) =>
                    handleChange({
                      target: {
                        name: "Taxpayer type",
                        value: e.target.value,
                        type: "select-one",
                      },
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: 4,
                    border: "1px solid #ccc",
                  }}
                >
                  <option value="">---Select---</option>
                  <option value="SSN/ITIN">SSN/ITIN</option>
                  <option value="EIN">EIN</option>
                </select>
              </div>
              <div style={{ marginBottom: 16 }}>
                <FormInput
                  label="TIN:"
                  id="Form-8233-Part_1_us_tax_id"
                  name="Form-8233-Part_1_us_tax_id"
                  value={(formData["Form-8233-Part_1_us_tax_id"] as string) || ""}
                  onChange={handleChange}
                  placeholder={
                    formData["Taxpayer type"] === "EIN"
                      ? "XX-XXXXXXX"
                      : formData["Taxpayer type"] === "SSN/ITIN"
                        ? "XXX-XX-XXXX"
                        : "Enter TIN"
                  }
                />
                {tinValidationMessage && (
                  <div
                    style={{
                      marginTop: 4,
                      padding: "6px 8px",
                      backgroundColor: tinValidationMessage.includes("✓") ? "#d4edda" : "#fff3cd",
                      border: `1px solid ${tinValidationMessage.includes("✓") ? "#28a745" : "#ffc107"}`,
                      borderRadius: 4,
                      fontSize: "12px",
                      color: tinValidationMessage.includes("✓") ? "#155724" : "#856404",
                    }}
                  >
                    {tinValidationMessage}
                  </div>
                )}
              </div>
              <FormInput
                label="Foreign taxpayer id:"
                id="Form-8233-Part_1_ftin"
                name="Form-8233-Part_1_ftin"
                value={(formData["Form-8233-Part_1_ftin"] as string) || ""}
                onChange={handleChange}
              />
            </Section>

            {/* Exemption Information */}
            <Section title="This exemption is applicable for compensation for">
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 4 }}>Calendar year:</label>
                <label style={{ color: "#666", display: "block", marginBottom: 8 }}>
                  (Enter date format: yyyy)
                </label>
                <FormInput
                  maxLength="4"
                  label=""
                  id="Form-8233-Calendar_year"
                  name="Form-8233-Calendar_year"
                  value={(formData["Form-8233-Calendar_year"] as string) || ""}
                  onChange={handleChange}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 4 }}>Tax year beginning:</label>
                <label style={{ color: "#666", display: "block", marginBottom: 8 }}>
                  (Enter date format:mm/dd/yyyy)
                </label>
                <FormDate
                  label=""
                  name="Form-8233-Beginning_year"
                  value={(formData["Form-8233-Beginning_year"] as string) || ""}
                  onChange={handleChange}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 4 }}>Tax year ending:</label>
                <label style={{ color: "#666", display: "block", marginBottom: 8 }}>
                  (Enter date format: mm/dd/yyyy)
                </label>
                <FormDate
                  label=""
                  name="Form-8233-Ending_year"
                  value={(formData["Form-8233-Ending_year"] as string) || ""}
                  onChange={handleChange}
                />
              </div>
            </Section>

            {/* Other Part I Information */}
            <Section title="Other Part I information">
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 4 }}>U.S. visa type:</label>
                <FormInput
                  label=""
                  id="Form-8233-Part_1_us_visa_type"
                  name="Form-8233-Part_1_us_visa_type"
                  value={(formData["Form-8233-Part_1_us_visa_type"] as string) || ""}
                  onChange={handleChange}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 4 }}>
                  Country issuing passport:
                </label>
                <CountryDropdown
                  label=""
                  name="Form-8233-Part_1_passport_country"
                  id="Form-8233-Part_1_passport_country"
                  value={
                    typeof formData["Form-8233-Part_1_passport_country"] === "string"
                      ? formData["Form-8233-Part_1_passport_country"]
                      : ""
                  }
                  onChange={(e) =>
                    handleChange({
                      target: {
                        name: "Form-8233-Part_1_passport_country",
                        value: e.target.value,
                        type: "select-one",
                      },
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 4 }}>Other:</label>
                <FormInput
                  label=""
                  id="Other"
                  name="Other"
                  value={(formData["Other"] as string) || ""}
                  onChange={handleChange}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 4 }}>Passport number:</label>
                <FormInput
                  label=""
                  id="Form-8233-Part_1_passport_number"
                  name="Form-8233-Part_1_passport_number"
                  value={(formData["Form-8233-Part_1_passport_number"] as string) || ""}
                  onChange={handleChange}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 4 }}>
                  Date of entry into the United States:
                </label>
                <label style={{ color: "#666", display: "block", marginBottom: 8 }}>
                  (Enter in U.S. date format: mm/dd/yyyy)
                </label>
                <FormDate
                  label=""
                  name="Form-8233-Part_1_date_entry_to_us"
                  value={(formData["Form-8233-Part_1_date_entry_to_us"] as string) || ""}
                  onChange={handleChange}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 4 }}>
                  Current nonimmigrant status:
                </label>
                <FormInput
                  label=""
                  id="Form-8233-Part_1_current_nonimmigrant_status"
                  name="Form-8233-Part_1_current_nonimmigrant_status"
                  value={(formData["Form-8233-Part_1_current_nonimmigrant_status"] as string) || ""}
                  onChange={handleChange}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 4 }}>
                  Current nonimmigrant status Not Applicable:
                </label>
                <input
                  type="checkbox"
                  id="Current nonimmigrant status Not Applicable"
                  name="Current nonimmigrant status Not Applicable"
                  checked={!!formData["Current nonimmigrant status Not Applicable"]}
                  onChange={(e) =>
                    handleChange({
                      target: {
                        name: "Current nonimmigrant status Not Applicable",
                        type: "checkbox",
                        checked: e.target.checked,
                      },
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                  style={{ marginRight: 8 }}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 4 }}>
                  Date your current nonimmigrant status expires:
                </label>
                <label style={{ color: "#666", display: "block", marginBottom: 8 }}>
                  (Enter in U.S. date format: mm/dd/yyyy)
                </label>
                <FormDate
                  label=""
                  name="Form-8233-Part_1_current_nonimmigrant_status_expires"
                  value={
                    (formData["Form-8233-Part_1_current_nonimmigrant_status_expires"] as string) ||
                    ""
                  }
                  onChange={handleChange}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 4 }}>
                  Foreign student, trainee, or researcher:
                </label>
                <FormCheckBox
                  label=""
                  name="Form-8233-Part_1_foreign_student_trainee_researcher"
                  checked={
                    (formData["Form-8233-Part_1_foreign_student_trainee_researcher"] as string) ||
                    ""
                  }
                  onChange={handleChange}
                />
              </div>
            </Section>

            {/* Part II Claim for Tax Treaty Withholding Exemption */}
            <Section title="Part II Claim for Tax Treaty Withholding Exemption and/or Personal Exemption Amount">
              {/* Section 11 */}
              <div style={{ marginBottom: 24 }}>
                <label style={{ fontSize: 16, display: "block", marginBottom: 16 }}>
                  11. Compensation for independent (and certain dependent) personal services:
                </label>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: "block", marginBottom: 8 }}>
                    a. Description of personal services you are providing:
                  </label>
                  <textarea
                    id="Form-8233-Part_2_personal_services"
                    name="Form-8233-Part_2_personal_services"
                    value={(formData["Form-8233-Part_2_personal_services"] as string) || ""}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleChange(e as any)}
                    style={{
                      width: "100%",
                      minHeight: 100,
                      borderRadius: 4,
                      border: "1px solid #ccc",
                      padding: 8,
                    }}
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: "block", marginBottom: 8 }}>
                    b. Total compensation you expect to be paid for these services in this calendar
                    or tax year ($):
                  </label>
                  <FormInput
                    label=""
                    id="Form-8233-Part_2_total_compensation_expected"
                    name="Form-8233-Part_2_total_compensation_expected"
                    value={
                      (formData["Form-8233-Part_2_total_compensation_expected"] as string) || ""
                    }
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* Section 12 */}
              <div style={{ marginBottom: 24 }}>
                <label style={{ fontSize: 16, display: "block", marginBottom: 16 }}>
                  12. If compensation is exempt from withholding based on a tax treaty benefit,
                  provide:
                </label>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: "block", marginBottom: 8 }}>
                    a. Tax treaty and treaty article on which you are basing exemption from
                    withholding:
                  </label>
                  <label style={{ display: "block", marginBottom: 4 }}>Tax treaty country:</label>
                  <CountryDropdown
                    label=""
                    name="Form-8233-Part_2_tax_treaty"
                    id="Form-8233-Part_2_tax_treaty"
                    value={
                      typeof formData["Form-8233-Part_2_tax_treaty"] === "string"
                        ? formData["Form-8233-Part_2_tax_treaty"]
                        : ""
                    }
                    onChange={(e) =>
                      handleChange({
                        target: {
                          name: "Form-8233-Part_2_tax_treaty",
                          value: e.target.value,
                          type: "select-one",
                        },
                      } as React.ChangeEvent<HTMLInputElement>)
                    }
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: "block", marginBottom: 8 }}>
                    b. Tax treaty article:
                  </label>
                  <FormInput
                    label=""
                    id="Form-8233-Part_2_treaty_article"
                    name="Form-8233-Part_2_treaty_article"
                    value={(formData["Form-8233-Part_2_treaty_article"] as string) || ""}
                    onChange={handleChange}
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: "block", marginBottom: 8 }}>
                    c. Total compensation that is exempt from tax under this treaty ($):
                  </label>
                  <FormInput
                    label=""
                    id="Form-8233-Part_2_total_compensation_listed_tax_under_treaty"
                    name="Form-8233-Part_2_total_compensation_listed_tax_under_treaty"
                    value={
                      (formData[
                        "Form-8233-Part_2_total_compensation_listed_tax_under_treaty"
                      ] as string) || ""
                    }
                    onChange={handleChange}
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: "block", marginBottom: 8 }}>
                    All is exempt from tax under this treaty:
                  </label>
                  <input
                    type="checkbox"
                    id="12_All is exempt from tax"
                    name="12_All is exempt from tax"
                    checked={!!formData["12_All is exempt from tax"]}
                    onChange={(e) =>
                      handleChange({
                        target: {
                          name: "12_All is exempt from tax",
                          type: "checkbox",
                          checked: e.target.checked,
                        },
                      } as React.ChangeEvent<HTMLInputElement>)
                    }
                    style={{ marginRight: 8 }}
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: "block", marginBottom: 4 }}>
                    d. Country of permanent residence:
                  </label>
                  <CountryDropdown
                    label=""
                    name="Form-8233-Part_2_country_residence"
                    id="Form-8233-Part_2_country_residence"
                    value={
                      typeof formData["Form-8233-Part_2_country_residence"] === "string"
                        ? formData["Form-8233-Part_2_country_residence"]
                        : ""
                    }
                    onChange={(e) =>
                      handleChange({
                        target: {
                          name: "Form-8233-Part_2_country_residence",
                          value: e.target.value,
                          type: "select-one",
                        },
                      } as React.ChangeEvent<HTMLInputElement>)
                    }
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: "block", marginBottom: 8 }}>Other:</label>
                  <FormInput
                    label=""
                    id="12d_Other"
                    name="12d_Other"
                    value={(formData["12d_Other"] as string) || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* Section 13 */}
              <div style={{ marginBottom: 24 }}>
                <label style={{ fontSize: 16, display: "block", marginBottom: 16 }}>
                  13. Noncompensatory scholarship or fellowship income:
                </label>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: "block", marginBottom: 8 }}>a. Amount ($):</label>
                  <FormInput
                    label=""
                    id="Form-8233-Part_2_scholarship_amount"
                    name="Form-8233-Part_2_scholarship_amount"
                    value={(formData["Form-8233-Part_2_scholarship_amount"] as string) || ""}
                    onChange={handleChange}
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: "block", marginBottom: 8 }}>
                    b. Tax treaty and treaty article on which you are basing exemption from
                    withholding:
                  </label>
                  <label style={{ display: "block", marginBottom: 4 }}>Tax treaty country:</label>
                  <CountryDropdown
                    label=""
                    name="Form-8233-Part_3_scholarship_tax_treaty"
                    id="Form-8233-Part_3_scholarship_tax_treaty"
                    value={
                      typeof formData["Form-8233-Part_3_scholarship_tax_treaty"] === "string"
                        ? formData["Form-8233-Part_3_scholarship_tax_treaty"]
                        : ""
                    }
                    onChange={(e) =>
                      handleChange({
                        target: {
                          name: "Form-8233-Part_3_scholarship_tax_treaty",
                          value: e.target.value,
                          type: "select-one",
                        },
                      } as React.ChangeEvent<HTMLInputElement>)
                    }
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: "block", marginBottom: 8 }}>
                    c. Tax treaty article:
                  </label>
                  <FormInput
                    label=""
                    id="Form-8233-Part_3_scholarship_treaty_article"
                    name="Form-8233-Part_3_scholarship_treaty_article"
                    value={
                      (formData["Form-8233-Part_3_scholarship_treaty_article"] as string) || ""
                    }
                    onChange={handleChange}
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: "block", marginBottom: 8 }}>
                    d. Total income that is exempt from tax under this treaty ($):
                  </label>
                  <FormInput
                    label=""
                    id="Form-8233-Part_3_scholarship_total_income_under_treaty"
                    name="Form-8233-Part_3_scholarship_total_income_under_treaty"
                    value={
                      (formData[
                        "Form-8233-Part_3_scholarship_total_income_under_treaty"
                      ] as string) || ""
                    }
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* Section 14 */}
              <div style={{ marginBottom: 24 }}>
                <label style={{ fontSize: 16, display: "block", marginBottom: 16 }}>
                  14. Sufficient facts to justify the exemption from withholding:
                </label>
                <textarea
                  id="Form-8233-Part_2_sufficient_facts"
                  name="Form-8233-Part_2_sufficient_facts"
                  value={(formData["Form-8233-Part_2_sufficient_facts"] as string) || ""}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleChange(e as any)}
                  style={{
                    width: "100%",
                    minHeight: 100,
                    borderRadius: 4,
                    border: "1px solid #ccc",
                    padding: 8,
                  }}
                />
              </div>
            </Section>

            {/* Certification */}
            <Section title="Certification">
              <div style={{ marginBottom: 24 }}>
                <div style={{ marginBottom: 8 }}>
                  <span className="form-label">
                    Is signed: <span style={{ color: "red" }}>*</span>
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="isSigned"
                      value="yes"
                      checked={formData.isSigned === "yes"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "isSigned", value: "yes" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    Yes
                  </label>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="isSigned"
                      value="no"
                      checked={formData.isSigned === "no"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "isSigned", value: "no" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    No
                  </label>
                </div>

                <div style={{ marginBottom: 8 }}>
                  <span className="form-label">
                    Has the form certification been modified:{" "}
                    <span style={{ color: "red" }}>*</span>
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="isModified"
                      value="yes"
                      checked={formData.isModified === "yes"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "isModified", value: "yes" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    Yes
                  </label>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="isModified"
                      value="no"
                      checked={formData.isModified === "no"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "isModified", value: "no" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    No
                  </label>
                </div>
                <FormInput
                  label="Signed Name:"
                  id="Form-8233-Part_3_signature"
                  name="Form-8233-Part_3_signature"
                  value={formData["Form-8233-Part_3_signature"] || ""}
                  onChange={handleChange}
                />

                <div style={{ marginBottom: "1rem" }}>
                  <FormDate
                    label="Date:"
                    name="Form-8233-Part_3_signature_date"
                    value={(formData["Form-8233-Part_3_signature_date"] as string) || ""}
                    onChange={handleChange}
                  />

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: 12,
                      marginTop: 12,
                    }}
                  >
                    <input
                      type="checkbox"
                      id="Signature_unreadable"
                      checked={!!formData["Signature_unreadable"]}
                      onChange={(e) =>
                        handleChange({
                          target: {
                            name: "Signature_unreadable",
                            type: "checkbox",
                            checked: e.target.checked,
                          },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                      style={{ marginRight: 8 }}
                    />
                    <label htmlFor="Signature_unreadable">Check if signature is unreadable</label>
                  </div>
                </div>
              </div>
            </Section>

            <div
              style={{
                display: "flex",
                marginTop: 20,
                marginBottom: 40,
                marginLeft: "auto",
                marginRight: "auto",
                width: "100%",
                maxWidth: "100%",
                alignItems: "center",
                boxSizing: "border-box",
              }}
            >
              <button
                type="submit"
                style={{
                  padding: "6px 16px",
                  backgroundColor: "var(--btn-primary-bg, #000000)",
                  color: "var(--btn-primary-text, white)",
                  fontWeight: 500,
                  marginLeft: "auto",
                  marginRight: "auto",
                  borderRadius: "0.375rem",
                  boxShadow: "none",
                  cursor: "pointer",
                  border: "none",
                }}
              >
                Save Form
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// Section component for consistent styling
const Section = ({ title, children }: { title: string; children: React.ReactNode }) => {
  return (
    <div style={sectionStyle}>
      <FormTitle value={title} />
      {children}
    </div>
  );
};

export default Form8233;
